import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import { SidebarCollapsibleSection } from 'components/blocks/layout/sidebar-section';
import {
  AgentGroupBy,
  readAgentGroupBy,
  storeAgentGroupBy,
} from 'components/blocks/layout/agents-group-menu';
import { cn } from 'lib/utils';
import { AgentSummary } from '../agent-chat-module/types/agent.service.type';
import { AgentChatAccordion } from './agent-chat-accordion';

const MAX_VISIBLE_AGENTS = 5;

type AgentsSectionProps = {
  agents?: AgentSummary[];
  chatId?: string;
  variant?: 'default' | 'section';
  renderRightAction?: (id: string, title: string) => ReactNode;
  groupBy?: AgentGroupBy;
  onGroupByChange?: (value: AgentGroupBy) => void;
  fill?: boolean;
};

type AgentsListProps = {
  agents: AgentSummary[];
  chatId?: string;
  variant: 'default' | 'section';
  renderRightAction?: (id: string, title: string) => ReactNode;
  fill?: boolean;
};

export function AgentsSection({
  agents = [],
  chatId,
  variant = 'section',
  renderRightAction,
  groupBy: groupByProp,
  onGroupByChange,
  fill = false,
}: AgentsSectionProps) {
  const { t } = useTranslation();
  const [storedGroupBy, setStoredGroupBy] = useState<AgentGroupBy>(readAgentGroupBy);
  const groupBy = groupByProp ?? storedGroupBy;

  const handleGroupByChange = (next: AgentGroupBy) => {
    if (onGroupByChange) {
      onGroupByChange(next);
      return;
    }
    setStoredGroupBy(next);
    storeAgentGroupBy(next);
  };

  if (agents.length === 0 && !onGroupByChange) {
    return null;
  }

  return (
    <SidebarCollapsibleSection
      title={t('AGENTS', 'Agents')}
      variant={variant}
      defaultOpen
      fill={fill}
      groupBy={variant === 'section' ? groupBy : undefined}
      onGroupByChange={variant === 'section' ? handleGroupByChange : undefined}
      tourTarget={variant === 'section' ? 'agents' : undefined}
    >
      {agents.length > 0 && (
        <AgentsList
          agents={agents}
          chatId={chatId}
          variant={variant}
          renderRightAction={renderRightAction}
          fill={fill}
        />
      )}
    </SidebarCollapsibleSection>
  );
}

function AgentsList({
  agents,
  chatId,
  variant,
  renderRightAction,
  fill = false,
}: AgentsListProps) {
  const [searchParams] = useSearchParams();
  const activeAgentIdFromUrl = searchParams.get('agent') || searchParams.get('agentId');

  const [openAgents, setOpenAgents] = useState<string[]>(() =>
    activeAgentIdFromUrl ? [activeAgentIdFromUrl] : [],
  );
  const [recentlyAccessedAgents, setRecentlyAccessedAgents] = useState<string[]>(() =>
    activeAgentIdFromUrl ? [activeAgentIdFromUrl] : [],
  );

  useEffect(() => {
    if (activeAgentIdFromUrl) {
      setOpenAgents((prev) =>
        prev.includes(activeAgentIdFromUrl) ? prev : [...prev, activeAgentIdFromUrl],
      );
    }
  }, [activeAgentIdFromUrl]);

  useEffect(() => {
    if (agents.length === 0) return;

    const validAgentIds = new Set(agents.map((agent) => agent.id));
    setRecentlyAccessedAgents((prev) => {
      const kept = prev.filter((id) => validAgentIds.has(id));
      const next =
        activeAgentIdFromUrl && validAgentIds.has(activeAgentIdFromUrl) && kept[0] !== activeAgentIdFromUrl
          ? [activeAgentIdFromUrl, ...kept.filter((id) => id !== activeAgentIdFromUrl)].slice(
              0,
              MAX_VISIBLE_AGENTS,
            )
          : kept;
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) return prev;
      return next;
    });
    setOpenAgents((prev) => {
      const next = prev.filter((id) => validAgentIds.has(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) return prev;
      return next;
    });
  }, [agents, activeAgentIdFromUrl]);

  const handleExpandAgent = useCallback((agentId: string) => {
    setOpenAgents((prev) => (prev.includes(agentId) ? prev : [...prev, agentId]));
  }, []);

  const recentlyAccessed = recentlyAccessedAgents
    .map((id) => agents.find((agent) => agent.id === id))
    .filter((agent): agent is AgentSummary => Boolean(agent));
  const otherAgents = agents.filter((agent) => !recentlyAccessedAgents.includes(agent.id));
  const orderedAgents = [...recentlyAccessed, ...otherAgents];

  return (
    <div
      data-testid="agents-list"
      className={cn(
        'flex flex-col gap-1',
        fill &&
          'min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:hsl(var(--grey-200))_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-grey-200'
      )}
    >
      {orderedAgents.map((agent) => (
        <AgentChatAccordion
          key={agent.id}
          agent={agent}
          chatId={chatId}
          variant={variant}
          isOpen={variant === 'section' ? openAgents.includes(agent.id) : undefined}
          onActiveChatFound={handleExpandAgent}
          renderRightAction={renderRightAction}
        />
      ))}
    </div>
  );
}
